import React from "react";
import { t } from "../../../components/CustomComponent.js";
import { typeMeta, isLandType, parkingTypes, parkingCount, decorationMeta, furnishMeta } from "../../../components/asset_option.js";
import ICON from "../icons.js";
import { fullAddress, mapLink, isRent, isSale, monthWord, directionWord } from "../property_util.js";

/* ============================================================================
 * PropertySide — คอลัมน์ขวาของหน้ารายละเอียด (ติดหน้าจอตามการเลื่อน)
 *   1. การ์ดผู้ติดต่อ — ปุ่ม LINE / โทร / ลงทะเบียนสนใจทรัพย์
 *   2. ตารางข้อมูลทรัพย์ — ค่าที่แอดมินกรอกไว้ในฟอร์ม แสดงเป็นคู่ ป้าย-ค่า
 *   3. ทำเลที่ตั้ง — แผนที่ย่อ + ที่อยู่ + ลิงก์เปิด Google Maps
 *
 * ผู้ติดต่อที่แสดงคือ "ผู้ดูแลประกาศ" ไม่ใช่เจ้าของทรัพย์ ข้อมูลเจ้าของ
 * (owner_*) เป็นข้อมูลภายใน ห้ามแสดงบนหน้าเว็บสาธารณะ
 * ========================================================================== */

/* แผนที่ย่อแบบวาดเอง — หน้าเว็บสาธารณะยังไม่โหลด Google Maps SDK เพื่อกันโหลดช้า
   TODO: สลับเป็น Static Maps API หรือ <GoogleMap> เมื่อเปิดใช้คีย์กับเว็บหน้าบ้าน */
const MiniMap = () => (
  <svg className="hm-pd-map__canvas" viewBox="0 0 320 200" role="img" aria-label={t("detail_location")}>
    <rect width="320" height="200" fill="#eef3ec" />
    <path d="M0 132 H320" stroke="#dfe7dc" strokeWidth="22" />
    <path d="M188 0 V200" stroke="#dfe7dc" strokeWidth="18" />
    <path d="M0 62 H320" stroke="#e6ede3" strokeWidth="10" />
    <path d="M74 0 V200" stroke="#e6ede3" strokeWidth="8" />
    <path d="M0 132 H320" stroke="#ffffff" strokeWidth="2" strokeDasharray="10 9" opacity="0.9" />
    <rect x="16" y="20" width="44" height="30" rx="4" fill="#dde6d9" />
    <rect x="96" y="16" width="70" height="34" rx="4" fill="#e3ebdf" />
    <rect x="210" y="24" width="58" height="26" rx="4" fill="#dde6d9" />
    <rect x="24" y="150" width="34" height="34" rx="4" fill="#e3ebdf" />
    <rect x="230" y="148" width="62" height="38" rx="4" fill="#dde6d9" />
    <circle cx="150" cy="106" r="26" fill="#a3e635" opacity="0.22" />
    <circle cx="150" cy="106" r="15" fill="#a3e635" opacity="0.32" />
    <path
      d="M150 82c-8.8 0-16 7.2-16 16 0 11.6 14.3 25.6 14.9 26.2a1.6 1.6 0 0 0 2.2 0c.6-.6 14.9-14.6 14.9-26.2 0-8.8-7.2-16-16-16zm0 22a6 6 0 1 1 0-12 6 6 0 0 1 0 12z"
      fill="#dc2626"
    />
  </svg>
);

/* ตัวอักษรย่อบนอวาตาร์ — ตัดคำนำหน้า "คุณ" ออกก่อน
   ภาษาไทยมีสระหน้า (เ แ โ ใ ไ) ที่อยู่ตัวเดียวแล้วอ่านไม่ออก เช่น "แอม" -> "แ"
   เจอกรณีนี้จึงหยิบมา 2 ตัวอักษรให้เป็น "แอ" */
const initial = (name) => {
  const clean = String(name || "").replace(/^คุณ\s*/, "").trim();
  return /^[เแโใไ]/.test(clean) ? clean.slice(0, 2) : clean.slice(0, 1);
};

/* 1 แถวของตารางข้อมูล */
const Row = ({ label, value }) =>
  value ? (
    <div className="hm-pd-kv">
      <span className="hm-pd-kv__k">{label}</span>
      <span className="hm-pd-kv__v">{value}</span>
    </div>
  ) : null;

export default function PropertySide({ data, agent, onLine, onCall, onAppointment }) {
  const meta = typeMeta(data.type);
  const direction = directionWord(data.direction);
  const land = isLandType(data.type);
  const deal = [isRent(data.deals) && t("for_rent"), isSale(data.deals) && t("for_sale")].filter(Boolean).join(" / ");

  return (
    <aside className="hm-pd-side">
      {/* --------------------------------------------------- การ์ดผู้ติดต่อ */}
      <div className="hm-pd-card hm-pd-agent">
        <div className="hm-pd-card__title">{t("detail_contact_title")}</div>

        <div className="hm-pd-agent__row">
          <span className={`hm-pd-agent__avatar is-tone-${agent.tone || 0}`}>
            {initial(agent.nickname)}
            {!!agent.verified && (
              <span className="hm-pd-agent__verified hm-ico" title={t("detail_agent_verified")}>
                {ICON.check_circle}
              </span>
            )}
          </span>

          <div className="hm-pd-agent__info">
            <div className="hm-pd-agent__name">{agent.nickname}</div>
            <div className="hm-pd-agent__company">{agent.company}</div>
            {!!agent.rating && (
              <div className="hm-pd-agent__rating">
                <span className="hm-ico">{ICON.star}</span>
                {agent.rating}
                <span className="hm-pd-agent__reviews">
                  ({agent.review_count} {t("detail_agent_reviews")})
                </span>
              </div>
            )}
          </div>
        </div>

        {!!agent.reply_time && (
          <span className="hm-pd-agent__reply">
            <span className="hm-ico">{ICON.bolt}</span>
            {t(agent.reply_time)}
          </span>
        )}

        <div className="hm-pd-agent__contacts">
          <span className="hm-pd-agent__contact">
            <span className="hm-ico">{ICON.phone}</span>
            {agent.phone}
          </span>
          {/* ระบบยังไม่ได้เก็บ LINE ของผู้ดูแลประกาศ — ซ่อนบรรทัดถ้าไม่มีค่า
              ไม่งั้นจะเห็นไอคอนลอยอยู่โดยไม่มีข้อมูล */}
          {!!agent.line && (
            <span className="hm-pd-agent__contact">
              <span className="hm-ico">{ICON.comment}</span>
              {agent.line}
            </span>
          )}
        </div>

        <div className="hm-pd-agent__actions">
          {!!agent.line && (
            <button type="button" className="hm-pd-btn hm-pd-btn--line" onClick={onLine}>
              <span className="hm-ico">{ICON.comment}</span>
              {t("detail_contact_line")}
            </button>
          )}
          <button type="button" className="hm-pd-btn hm-pd-btn--outline" onClick={onCall}>
            <span className="hm-ico">{ICON.phone}</span>
            {t("detail_contact_call")}
          </button>
          {/* ปุ่มหลักของการ์ด — ฝากข้อมูลเข้าระบบได้ทันทีแม้นอกเวลาทำการ
              (โทร/LINE ต้องรอผู้ดูแลว่าง จึงลดเป็นปุ่มรอง) */}
          <button type="button" className="hm-pd-btn hm-pd-btn--primary" onClick={onAppointment}>
            <span className="hm-ico">{ICON.calendar}</span>
            {t("detail_contact_lead")}
          </button>
        </div>

        <p className="hm-pd-agent__note">
          <span className="hm-ico">{ICON.shield}</span>
          {t("detail_contact_note")}
        </p>
      </div>

      {/* ------------------------------------------------- ตารางข้อมูลทรัพย์ */}
      <div className="hm-pd-card">
        <div className="hm-pd-card__title">{t("detail_info_title")}</div>

        <div className="hm-pd-kvs">
          <Row label={t("property_type")} value={t(meta.label)} />
          <Row label={t("detail_deal_type")} value={deal} />
          <Row label={t("project")} value={data.project} />
          {!land && <Row label={t("detail_building")} value={data.building} />}
          {!land && <Row label={t("floor_label")} value={data.floor} />}
          {/* บ้านแนวราบมีทั้งที่ดินและพื้นที่ใช้สอย จึงโชว์ที่ดินด้วยเมื่อกรอกไว้ */}
          {(land || !!data.land_area) && (
            <Row label={t("land_area")} value={data.land_area && `${Number(data.land_area).toLocaleString()} ${t("sqw")}`} />
          )}
          {!land && <Row label={t("usable_area")} value={data.area && `${data.area} ${t("sqm")}`} />}
          {!land && <Row label={t("floor_count")} value={data.floor_count && `${data.floor_count} ${t("floor_label")}`} />}
          {!land && <Row label={t("bedroom")} value={data.bedroom && `${data.bedroom} ${t("room_unit")}`} />}
          {!land && <Row label={t("bathroom")} value={data.bathroom && `${data.bathroom} ${t("room_unit")}`} />}
          {!land && <Row label={t("living_room")} value={data.living_room && `${data.living_room} ${t("room_unit")}`} />}
          {!land && <Row label={t("kitchen")} value={data.kitchen && `${data.kitchen} ${t("room_unit")}`} />}
          {/* ที่จอดรถบอกเป็นประเภทที่จอดได้ ระบุจำนวนคันไว้จึงต่อท้าย */}
          {!land && (
            <Row
              label={t("detail_parking")}
              value={parkingTypes(data)
                .map((e) => (parkingCount(data, e) ? `${t(e.label)} ${parkingCount(data, e)} ${t("car_unit")}` : t(e.label)))
                .join(" · ")}
            />
          )}
          <Row label={t("direction")} value={direction} />
          {/* การตกแต่ง / เฟอร์นิเจอร์ เก็บเป็นรหัส จึงต้องหา label ก่อนค่อยแปล */}
          {!land && <Row label={t("decoration")} value={decorationMeta(data.decoration) && t(decorationMeta(data.decoration).label)} />}
          {!land && <Row label={t("detail_furnished")} value={furnishMeta(data.furnished) && t(furnishMeta(data.furnished).label)} />}
          {!land && <Row label={t("pet")} value={t(data.pet_allowed ? "pet_allowed" : "pet_not_allowed")} />}
          {!land && <Row label={t("built_year")} value={data.built_year} />}
          {isRent(data.deals) && (
            <Row label={t("detail_min_contract")} value={monthWord(data.min_contract_month)} />
          )}
          <Row
            label={t("status")}
            value={<span className={`hm-pd-status is-${data.status}`}>{t(`status_${data.status}`)}</span>}
          />
          <Row label={t("detail_available_at")} value={data.available_at && t(data.available_at)} />
        </div>
      </div>

      {/* -------------------------------------------------------- ทำเลที่ตั้ง */}
      <div className="hm-pd-card">
        <div className="hm-pd-card__title">{t("detail_location")}</div>

        <div className="hm-pd-map">
          <MiniMap />
          <a
            className="hm-pd-map__link"
            href={mapLink(data)}
            target="_blank"
            rel="noreferrer"
          >
            <span className="hm-ico">{ICON.map}</span>
            {t("detail_open_maps")}
          </a>
        </div>

        <div className="hm-pd-addr">
          <span className="hm-pd-addr__zone">
            <span className="hm-ico">{ICON.marker}</span>
            {data.zone}
          </span>
          <span className="hm-pd-addr__full">{fullAddress(data)}</span>
        </div>

        <p className="hm-pd-map__note">
          <span className="hm-ico">{ICON.info}</span>
          {t("detail_location_note")}
        </p>
      </div>
    </aside>
  );
}
